import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8090/api';

const pbApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

pbApi.interceptors.request.use((config) => {
  const tokenKey = process.env.REACT_APP_TOKEN_KEY || 'client_token';
  const token = localStorage.getItem(tokenKey);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface TrackerColumn {
  name: string;
  statusIds: string[];
}

/** Client-facing stages — clubs related Jira board columns into a short tracker */
export interface TrackerStage {
  name: string;
  /** Column names (from board) included in this stage */
  columnNames: string[];
}

export interface TaxReturnTicketTracker {
  issueKey: string;
  issueId: string;
  summary: string;
  statusName: string;
  statusId: string;
  /** Jira "Tax Year" field — commas stripped for display */
  taxYear?: string;
  /** Exact Jira board column */
  currentColumnIndex: number;
  currentColumnName: string;
  /** Clubbed client-facing stage */
  currentStageIndex: number;
  currentStageName: string;
  stages: TrackerStage[];
  columns: TrackerColumn[];
}

export interface TaxReturnTrackerResult {
  redRockId: string;
  entityName: string;
  columns: TrackerColumn[];
  stages: TrackerStage[];
  tickets: TaxReturnTicketTracker[];
}

/**
 * Portal stages (order matters for the stepper).
 * Exact Jira board column / status names only — unmapped columns are ignored.
 */
export const STAGE_COLUMN_MAP: { name: string; columns: string[] }[] = [
  {
    name: 'staging',
    columns: ['Ready for Prep', 'New Client'],
  },
  {
    name: 'Need Additional Information',
    columns: [
      'Needs Info',
      'Waiting on Customer',
      'Waiting on Internal',
      'Hold',
    ],
  },
  {
    name: 'In Progress',
    columns: ['WIP'],
  },
  {
    name: 'In Review',
    columns: ['Ready for Review', 'Review 1', 'Review 2'],
  },
  {
    name: 'Ready',
    columns: [
      'Unresponsive',
      'Processing Staging',
      'Sent, Manager',
      'Sent Manager',
      'Invoice',
      'Signature',
      'Printing',
      'Printed',
      'Auto ESS',
    ],
  },
  {
    name: 'Pending Confirmation',
    columns: ['E-file', 'Efile'],
  },
  {
    name: 'Accepted',
    columns: ['Final'],
  },
  {
    name: 'Rejected',
    columns: ['Reject'],
  },
];

function normalizeLabel(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** Title Case for UI display (e.g. staging → Staging). Matching still uses raw stage names. */
export function formatStageLabel(name: string): string {
  const value = String(name || '').trim();
  if (!value) return '';
  return value
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Portal display for Jira Tax Year — strip commas; numbers like 2025.0 → "2025". */
export function formatTaxYear(value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(Math.round(value));
  }
  if (Array.isArray(value)) {
    return value
      .map(formatTaxYear)
      .filter(Boolean)
      .join(' ')
      .replace(/,/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  if (typeof value === 'object') {
    const obj = value as { value?: unknown; name?: unknown; label?: unknown };
    return formatTaxYear(obj.value ?? obj.name ?? obj.label ?? '');
  }
  const s = String(value)
    .replace(/,/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (/^\d+\.0+$/.test(s)) return s.split('.')[0];
  return s;
}

function matchStageIndex(columnName: string): number {
  const name = normalizeLabel(columnName);
  if (!name) return -1;
  return STAGE_COLUMN_MAP.findIndex((stage) =>
    stage.columns.some((col) => normalizeLabel(col) === name)
  );
}

/** Club known Jira board columns into STAGE_COLUMN_MAP stages; ignore unknowns. */
export function buildClientStages(columns: TrackerColumn[]): TrackerStage[] {
  return STAGE_COLUMN_MAP.map((def) => ({
    name: def.name,
    columnNames: (columns || [])
      .map((col) => col.name)
      .filter((colName) =>
        def.columns.some((mapped) => normalizeLabel(mapped) === normalizeLabel(colName))
      ),
  }));
}

export function stageIndexForColumnName(
  columnName: string,
  stages: TrackerStage[],
  _columns?: TrackerColumn[]
): number {
  const byMembership = stages.findIndex((stage) =>
    stage.columnNames.some((n) => normalizeLabel(n) === normalizeLabel(columnName))
  );
  if (byMembership >= 0) return byMembership;

  return matchStageIndex(columnName);
}

function normalizeColumns(raw: any[]): TrackerColumn[] {
  return (raw || []).map((col) => ({
    name: String(col?.name || ''),
    statusIds: Array.isArray(col?.statusIds)
      ? col.statusIds.map((id: any) => String(id))
      : [],
  }));
}

function normalizeTicket(
  raw: any,
  stages: TrackerStage[],
  columns: TrackerColumn[]
): TaxReturnTicketTracker | null {
  if (!raw || typeof raw !== 'object') return null;
  const issueKey = String(raw.issueKey || raw.key || '').trim();
  if (!issueKey) return null;

  const currentColumnName = String(
    raw.currentColumnName || raw.statusName || ''
  );
  const currentColumnIndex =
    Number(raw.currentColumnIndex ?? columns.findIndex((c) => c.name === currentColumnName)) ||
    0;
  const currentStageIndex = stageIndexForColumnName(
    currentColumnName || String(raw.statusName || ''),
    stages,
    columns
  );

  // Ignore tickets whose Jira column/status is not in STAGE_COLUMN_MAP
  if (currentStageIndex < 0) {
    return null;
  }

  return {
    issueKey,
    issueId: String(raw.issueId || raw.id || ''),
    summary: String(raw.summary || issueKey),
    statusName: String(raw.statusName || 'Unknown'),
    statusId: String(raw.statusId || ''),
    taxYear: formatTaxYear(raw.taxYear ?? raw.tax_year ?? ''),
    currentColumnIndex,
    currentColumnName: currentColumnName || String(raw.statusName || ''),
    currentStageIndex,
    currentStageName: stages[currentStageIndex]?.name || STAGE_COLUMN_MAP[0].name,
    stages,
    columns,
  };
}

/**
 * Tax return tracker via PocketBase custom route (same pattern as boxService /api/box/*).
 * Stages are always re-clubbed on the client from STAGE_COLUMN_MAP.
 *
 * GET /tax-return-tracker?redRockId=...&entityName=...
 */
export async function fetchTaxReturnTrackers(params: {
  entityName: string;
  customerRecordId?: string;
  boardId?: string;
}): Promise<TaxReturnTrackerResult> {
  const redRockId = String(params.customerRecordId || '').trim();
  const entityName = String(params.entityName || '').trim();

  if (!redRockId && !entityName) {
    throw new Error('Missing company selection for tax return tracker');
  }

  const query = new URLSearchParams();
  if (redRockId) query.set('redRockId', redRockId);
  if (entityName) query.set('entityName', entityName);
  if (params.boardId) query.set('boardId', params.boardId);

  const response = await pbApi.get(`/tax-return-tracker?${query.toString()}`);
  const data = response.data || {};

  if (data.success === false) {
    throw new Error(
      data.details || data.error || data.message || 'Failed to load tax return tracker'
    );
  }

  const columns = normalizeColumns(data.columns || []);
  const stages = buildClientStages(columns);
  const tickets = (Array.isArray(data.tickets) ? data.tickets : [])
    .map((t: any) => normalizeTicket(t, stages, columns))
    .filter(Boolean) as TaxReturnTicketTracker[];

  return {
    redRockId: String(data.redRockId || redRockId),
    entityName: String(data.entityName || entityName),
    columns,
    stages,
    tickets,
  };
}

const taxReturnTrackerService = {
  fetchTaxReturnTrackers,
  buildClientStages,
  formatStageLabel,
  formatTaxYear,
  STAGE_COLUMN_MAP,
};

export default taxReturnTrackerService;
